import React from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from "@heroui/react";

import { store, useAppSelector } from '@/app/store';
import { useSpeedTest, useWebSocketStatus } from '@/app/websocket/hooks';
import { readSpeedTestResult, speedTestErrorKey } from '@/app/websocket/speedTest';
import { getUnixEUTime, HotelRoom } from '@/features/projects/projectsSlice';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';

// Speed test result thresholds for color coding
const THRESHOLDS = {
  download: { ok: 5, high: 25 },      // Mbps: red < 5, yellow 5-25, green > 25
  upload:   { ok: 2, high: 10 },      // Mbps
  latency:  { ok: 100, high: 50 },    // ms: green < 50, yellow 50-100, red > 100
};

interface StatusBadge {
  label: string;
  color: string;    // 'red' | 'yellow' | 'green'
  bgColor: string;
  textColor: string;
}

function getStatusBadge(value: string | undefined, type: 'download' | 'upload' | 'latency'): StatusBadge {
  if (!value || value === '') {
    return { label: 'No data', color: 'red', bgColor: '#FEE2E2', textColor: '#DC2626' };
  }

  const num = parseFloat(value);
  if (isNaN(num)) {
    return { label: 'Invalid', color: 'red', bgColor: '#FEE2E2', textColor: '#DC2626' };
  }

  const thresh = THRESHOLDS[type];
  let color: 'red' | 'yellow' | 'green';
  let label: string;

  if (type === 'latency') {
    // For latency, lower is better: green < 50, yellow 50-100, red > 100
    if (num <= thresh.high) {
      color = 'green';
      label = 'Low';
    } else if (num <= thresh.ok) {
      color = 'yellow';
      label = 'Medium';
    } else {
      color = 'red';
      label = 'High';
    }
  } else {
    // For speed, higher is better: green > high, yellow ok-high, red < ok
    if (num >= thresh.high) {
      color = 'green';
      label = 'OK for HD';
    } else if (num >= thresh.ok) {
      color = 'yellow';
      label = 'OK for SD';
    } else {
      color = 'red';
      label = 'Too low';
    }
  }

  const bgColors = { red: '#FEE2E2', yellow: '#FEF08A', green: '#DCFCE7' };
  const textColors = { red: '#DC2626', yellow: '#B45309', green: '#15803D' };

  return {
    label,
    color,
    bgColor: bgColors[color],
    textColor: textColors[color],
  };
}

// Circular gauge showing download speed
const SpeedGauge = (props: { download: string | undefined; max?: number }) => {
  const value = props.download ? parseFloat(props.download) : 0;
  const max = props.max || 100;
  const percent = Math.min((value / max) * 100, 100);

  const radius = 45;
  const cx = 60, cy = 60;

  return (
    <div className="flex flex-col items-center justify-center w-[140px]">
      <svg width="140" height="140" viewBox="0 0 140 140" className="relative">
        {/* Background circle */}
        <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#E5E7EB" strokeWidth="8" />

        {/* Progress arc */}
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="#3B82F6"
          strokeWidth="8"
          strokeDasharray={`${(percent / 100) * 2 * Math.PI * radius} ${2 * Math.PI * radius}`}
          strokeLinecap="round"
          style={{
            transform: `rotate(-135deg)`,
            transformOrigin: `${cx}px ${cy}px`,
          }}
        />

        {/* Center text */}
        <text
          x={cx}
          y={cy - 4}
          textAnchor="middle"
          fontSize="24"
          fontWeight="bold"
          fill="#1F2937"
        >
          {value.toFixed(1)}
        </text>
        <text
          x={cx}
          y={cy + 12}
          textAnchor="middle"
          fontSize="11"
          fill="#6B7280"
          fontWeight="500"
        >
          Mbps
        </text>
        <text
          x={cx}
          y={cy + 28}
          textAnchor="middle"
          fontSize="10"
          fill="#9CA3AF"
        >
          DOWNLOAD
        </text>
      </svg>
    </div>
  );
};

// Result card with color-coded status badge
const ResultCard = (props: { label: string; value: string; unit: string; badge: StatusBadge }) => (
  <div className="flex flex-col gap-[8px] p-[12px] border border-[#E5E7EB] rounded-[8px]">
    <div className="text-[11px] font-medium text-[#6B7280] uppercase">
      {props.label}
    </div>
    <div className="text-[18px] font-bold text-[#1F2937]">
      {props.value}
      <span className="text-[12px] text-[#6B7280] ml-[4px]">{props.unit}</span>
    </div>
    <div
      className="text-[12px] font-medium"
      style={{ color: props.badge.textColor }}
    >
      {props.badge.label}
    </div>
  </div>
);

export interface RoomSpeedTestProps {
  projectId:		string;
  roomNo:			string;
  canStart:			boolean;
}

const RoomSpeedTest = (props: RoomSpeedTestProps) => {
  const { t } = useTranslation();
  const speedTest = useSpeedTest();
  const wsStatus = useWebSocketStatus();

  const fields = useAppSelector((state) => {
    const active = state.projects.active;
    if (!active || active.id !== props.projectId) return undefined;
    const room = active.rooms?.rooms?.find((r: HotelRoom) => r.room_no === props.roomNo);
    return room?.info?.fields;
  });
  const result = React.useMemo(() => readSpeedTestResult(fields), [fields]);

  const mine = speedTest.hotelId === props.projectId && speedTest.roomNo === props.roomNo;
  const running = mine && (speedTest.status === 'pending' || speedTest.status === 'ack');
  const busy = speedTest.status === 'pending' || speedTest.status === 'ack';

  const dlBadge = getStatusBadge(result?.download, 'download');
  const ulBadge = getStatusBadge(result?.upload, 'upload');
  const ltBadge = getStatusBadge(result?.latency, 'latency');

  return (
    <div className="w-full p-[16px] bg-white rounded-[8px] border border-[#E5E7EB]">
      {/* Header */}
      <div className="flex items-start justify-between mb-[16px]">
        <div className="flex flex-col gap-[4px]">
          <h3 className="text-[14px] font-bold text-black">
            {t('projects:project:rooms:info:speed_test')}
          </h3>
          <p className="text-[12px] text-[#6B7280]">
            {t('projects:project:rooms:info:speed_test_subtitle', { defaultValue: 'Network performance of this unit' })}
          </p>
        </div>
        {mine && speedTest.status === 'done' && (
          <div className="text-[12px] font-medium text-[#10B981] flex items-center gap-[4px]">
            ✓ {t('projects:project:rooms:info:speed_test_done')}
          </div>
        )}
      </div>

      {/* Main content */}
      {result && result.timestamp ? (
        <>
          <div className="flex gap-[20px] mb-[20px]">
            {/* Gauge - left side */}
            <div className="flex-shrink-0">
              <SpeedGauge download={result.download} max={100} />
            </div>

            {/* Result cards grid - right side */}
            <div className="flex-1 grid grid-cols-2 gap-[12px]">
              <ResultCard
                label={t('projects:project:rooms:info:speed_test_download')}
                value={result.download || '-'}
                unit="Mbps"
                badge={dlBadge}
              />
              <ResultCard
                label={t('projects:project:rooms:info:speed_test_upload')}
                value={result.upload || '-'}
                unit="Mbps"
                badge={ulBadge}
              />
              <ResultCard
                label={t('projects:project:rooms:info:speed_test_latency')}
                value={result.latency || '-'}
                unit="ms"
                badge={ltBadge}
              />
              <ResultCard
                label={t('projects:project:rooms:info:speed_test_result')}
                value={result.status ? t(`projects:project:rooms:info:speed_test_status:${result.status}`, { defaultValue: result.status }) : '-'}
                unit=""
                badge={result.status === 'success' ? { label: 'Success', color: 'green', bgColor: '#DCFCE7', textColor: '#15803D' } : { label: 'Error', color: 'red', bgColor: '#FEE2E2', textColor: '#DC2626' }}
              />
            </div>
          </div>

          {/* Last test time */}
          <div className="text-[12px] text-[#6B7280] mb-[16px] flex items-center gap-[4px]">
            🕐 {t('projects:project:rooms:info:speed_test_last')}: {getUnixEUTime(result.timestamp)}
          </div>
        </>
      ) : (
        <div className="text-[14px] text-[#6B7280] py-[16px]">
          {t('projects:project:rooms:info:speed_test_no_result', { defaultValue: 'No test results yet' })}
        </div>
      )}

      {/* Bottom section with button and status */}
      <div className="flex items-center justify-between pt-[16px] border-t border-[#E5E7EB]">
        <div className="flex items-center gap-[8px]">
          {running && <CircularProgress size="sm" />}
          {mine && speedTest.status === 'pending' && (
            <span className="text-[12px] text-[#6B7280]">
              {t('projects:project:rooms:info:speed_test_sending')}
            </span>
          )}
          {mine && speedTest.status === 'ack' && (
            <span className="text-[12px] text-[#3B82F6]">
              {t('projects:project:rooms:info:speed_test_running')}
            </span>
          )}
          {mine && speedTest.status === 'error' && (
            <span className="text-[12px] text-[#DC2626]">
              {t(speedTestErrorKey(speedTest.errorCode))}
            </span>
          )}
          {!busy && wsStatus !== 'connected' && !(mine && speedTest.status === 'error') && (
            <span className="text-[12px] text-[#F97316]">
              {t('projects:project:rooms:info:speed_test_errors:ws_not_connected')}
            </span>
          )}
        </div>

        {props.canStart && (
          <Button
            caption={t('projects:project:rooms:info:speed_test_run')}
            isDisabled={busy || wsStatus !== 'connected'}
            onClick={() => store.startSpeedTest(props.projectId, props.roomNo)}
          />
        )}
      </div>
    </div>
  );
};

export default RoomSpeedTest;
